import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { DataShape, Field, FormProps, reduxForm } from 'redux-form';

import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import * as loginUIAPI from 'login-apis/loginUIAPI';
import * as buyingPowerCalculatorForm from 'login-utils/buyingPowerCalculatorForm';
import * as formUtils from 'login-utils/formUtils';
import { paneFormStyles } from 'login-components/Common/PaneLayout/paneStyles';
import PaneFormHeader from 'login-components/Common/PaneLayout/PaneFormHeader';
import PrimaryButton from 'login-components/Common/Buttons/PrimaryButton';
import BuyingPowerInputSummary from './BuyingPowerInputSummary';
import BuyingPowerSummary from './BuyingPowerSummary';

const styles = StyleSheet.create({
  summaryWrapper: {
    marginTop: 30,
    maxWidth: 700,
  },
  expandButton: {
    height: 24,
    width: 24,
  },
  editButton: {
    marginTop: 15,
    maxWidth: 185,
  },
});

interface BuyingPowerState {
  buyingPower: BuyingPowerFields | null;
  showForm: boolean;
  error: string | null;
}

export class BuyingPowerCalculator extends React.Component<FormProps<DataShape, {}, ReduxState>, BuyingPowerState> {
  constructor(props) {
    super(props);
    this.state = {
      buyingPower: null,
      showForm: true,
      error: null,
    };
  }

  onSubmit = async values => {
    this.setState({
      error: null,
    });
    try {
      const { stateCode, countyName } = values.county;
      const buyingPowerInputValues = {
        ...values,
        county: {
          stateCode,
          countyName,
        },
      } as buyingPowerCalculatorForm.FIELDS;
      const buyingPower = await loginUIAPI.getBuyingPower(buyingPowerInputValues);
      this.setState({
        buyingPower,
        showForm: false,
      });
    } catch (err) {
      this.setState({
        error: 'There was an error fetching buying power. Please refresh and try again.',
      });
    }
  };

  toggleShowForm = () => {
    this.setState({
      showForm: !this.state.showForm,
    });
  };

  formSections = buyingPowerCalculatorForm.getFormSections();

  render() {
    const { handleSubmit } = this.props;

    return (
      <div className="BuyingPowerCalculatorForm">
        {!this.state.showForm && this.state.buyingPower && (
          <div data-it="BuyingPowerSummary" className={css(styles.summaryWrapper)}>
            <BuyingPowerInputSummary />
            <BuyingPowerSummary buyingPower={this.state.buyingPower} />
            <div className={css(styles.editButton)}>
              <PrimaryButton
                onClick={this.toggleShowForm}
                text="Edit Inputs"
                fontFamily="Roboto"
                data-it="BuyingPowerCalculator-edit"
              />
            </div>
          </div>
        )}
        {this.state.showForm && (
          <form className={css(paneFormStyles.wrapper)} noValidate onSubmit={handleSubmit!(this.onSubmit)}>
            {/* We wrap all the form sections in a div in order to select on the last one with :last-child and
            stop it from adding extra padding */}
            <div>
              {this.formSections.map(definition => (
                <div className={css(paneFormStyles.sectionWrapper)} key={definition.header.text}>
                  <PaneFormHeader {...definition.header} />
                  <div className={css(paneFormStyles.inputsWrapper)}>
                    {definition.inputs.map(input => (
                      <div className={css(paneFormStyles.fieldWrapper)} key={input.name}>
                        <Field {...input} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {this.state.error && (
              <div data-it="BuyingPowerError" className={css(paneFormStyles.errorWrapper)}>
                <ErrorMessage errorText={this.state.error} />
              </div>
            )}
            <div className={css(paneFormStyles.buttonPadding)}>
              <div className={css(paneFormStyles.buttonDisplay)}>
                <PrimaryButton isWide text="Submit" fontFamily="Roboto" data-it="BuyingPowerCalculator-submit" />
              </div>
            </div>
          </form>
        )}
      </div>
    );
  }
}

const BuyingPowerCalculatorForm = reduxForm<any, any>({
  ...buyingPowerCalculatorForm.FORM_CONFIG,
  validate: (values: DataShape) => formUtils.validate(buyingPowerCalculatorForm.getAllInputs(), values) as any,
})(BuyingPowerCalculator);

export default BuyingPowerCalculatorForm;
